import { cn } from '@/lib/utils';
import { fill, type LeaderboardCopy } from '../leaderboard-copy';
import { publicName } from '../leaderboard-ranking';
import type { LeaderboardEntry } from '../leaderboard.types';
import { ContributorAvatar } from '../table/ContributorAvatar';

interface LeaderboardPodiumProps {
  copy: LeaderboardCopy;
  entries: LeaderboardEntry[];
  isMine: (entry: LeaderboardEntry) => boolean;
  format: (value: number) => string;
  languageName: (code: string) => string;
}

/** The top three as plain cards: rank number, name, count. First in brand. */
export function LeaderboardPodium({ copy, entries, isMine, format, languageName }: LeaderboardPodiumProps) {
  if (entries.length === 0) return null;
  return (
    <section aria-label={copy.podiumLabel}>
      <ol className="grid gap-3 sm:grid-cols-3">
        {entries.map((entry) => {
          const first = entry.rank === 1;
          const mine = isMine(entry);
          const name = publicName(entry);
          return (
            <li
              key={entry.userId ?? entry.rank}
              className={cn(
                'flex items-center gap-3 rounded-lg border p-4 sm:flex-col sm:items-start sm:gap-4',
                first ? 'border-brand/30' : 'border-border/60',
                mine ? 'bg-brand/[0.06]' : 'bg-surface',
              )}
            >
              <div className="flex items-center gap-3 sm:w-full sm:justify-between">
                <span
                  className={cn(
                    'grid size-8 shrink-0 place-items-center rounded-full text-sm font-semibold tabular-nums',
                    first ? 'bg-brand/10 text-brand' : 'bg-surface-2 text-muted-foreground',
                  )}
                  aria-label={fill(copy.rankShort, { rank: entry.rank })}
                >
                  {entry.rank}
                </span>
                <ContributorAvatar name={name} className="hidden sm:grid" />
              </div>
              <div className="min-w-0 flex-1 sm:w-full">
                <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-foreground">
                  <span className="truncate">{name}</span>
                  {mine ? <span className="shrink-0 text-xs font-medium text-brand">{copy.you}</span> : null}
                </p>
                {entry.username && entry.username !== name ? (
                  <p className="truncate text-xs text-faint-foreground">@{entry.username}</p>
                ) : null}
                {entry.languages.length > 0 ? (
                  <p className="mt-1 truncate text-xs text-muted-foreground">{entry.languages.map(languageName).join(" · ")}</p>
                ) : null}
              </div>
              <p className="shrink-0 text-right sm:text-left">
                <span className={cn('block text-lg font-semibold tabular-nums sm:text-2xl', first ? 'text-brand' : 'text-foreground')}>
                  {format(entry.contributions)}
                </span>
                <span className="block text-xs text-muted-foreground">{copy.value}</span>
              </p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
